import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, TrendingUp, Eye, BellRing, ExternalLink } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useOrg } from '../../hooks/useOrg';
import { OWARE_URL } from '../../lib/oware';
import './Onboarding.css';
import { isFirmLike } from '../../lib/orgTypes';

// Post-checkout handoff. NPI + Stripe Identity already gate signup, payment
// just succeeded, so the only thing left worth showing is the OWARE perk that
// ships with every medical-practice subscription. Anything else here would be
// busywork or a placeholder.

// What the user gets from the OWARE perk. Shown as a 3-up bullet list so the
// pitch is concrete instead of a brand line.
const OWARE_BENEFITS = [
  {
    Icon: TrendingUp,
    title: 'Daily financial visibility',
    desc:  'Cash position, revenue, and EBITDA trends pulled from your books and refreshed every morning.',
  },
  {
    Icon: Eye,
    title: 'Owner-awareness reporting',
    desc:  'A single weekly digest that flags anomalies — slowing collections, payroll drift, vendor concentration.',
  },
  {
    Icon: BellRing,
    title: 'Fraud and irregularity alerts',
    desc:  'Real-time pings the moment a duplicate vendor payment, unusual transfer, or atypical refund hits the ledger.',
  },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useSubscription();
  const { currentOrg } = useOrg();
  const isFirm = isFirmLike(currentOrg?.type);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  // Refresh subscription state on mount — webhook may have just landed.
  useEffect(() => { refresh(); }, [refresh]);

  // Header fade-in trigger. Use IntersectionObserver with a setTimeout
  // fallback for tall viewports that don't fire it.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    obs.observe(node);
    const t = setTimeout(() => setInView(true), 1200);
    return () => { obs.disconnect(); clearTimeout(t); };
  }, []);

  const goDashboard = () => navigate('/app/dashboard', { replace: true });

  const handleOpen = () => {
    // The referral link carries the MediLink ref code; nothing else is
    // passed through from here.
    window.open(OWARE_URL, '_blank', 'noopener');
    goDashboard();
  };

  return (
    <div ref={sectionRef} className={`ob ob-solo ${inView ? 'is-in' : ''}`}>
      <header className="ob-header">
        <div className="ob-eyebrow">
          <span className="ob-dot" /> Welcome to MediLink
        </div>
        <h1 className="ob-title">
          You're <em>verified</em> and ready to go.
        </h1>
        <p className="ob-sub">
          {isFirm
            ? "Your firm and identity are confirmed. One last perk before you head to your dashboard — it's included free."
            : "Your NPI, identity, and payment are all confirmed. One last perk before you head to your dashboard — it's included with every subscription."}
        </p>
      </header>

      <div className="ob-card">
        <div className="ob-card-icon">
          <Shield size={28} strokeWidth={2.2} />
        </div>

        <div className="ob-card-eyebrow">{isFirm ? 'Included free with MediLink' : 'Included with your subscription'}</div>
        <h2 className="ob-card-title">Set up OWARE</h2>
        <p className="ob-card-desc">
          OWARE is the financial health and fraud-monitoring layer that ships
          free with MediLink. It connects to your accounting and banking once,
          then quietly watches the numbers for you — daily snapshots, weekly
          owner reports, and instant alerts when something looks off.
        </p>

        {/* What you get — three concrete benefits */}
        <ul className="ob-benefits">
          {OWARE_BENEFITS.map(b => (
            <li key={b.title} className="ob-benefit">
              <div className="ob-benefit-icon">
                <b.Icon size={16} strokeWidth={2.2} />
              </div>
              <div>
                <div className="ob-benefit-title">{b.title}</div>
                <div className="ob-benefit-desc">{b.desc}</div>
              </div>
            </li>
          ))}
        </ul>

        <div className="ob-card-actions">
          <button type="button" onClick={goDashboard} className="ob-btn ob-btn-skip">
            Skip for now
          </button>
          <button type="button" onClick={handleOpen} className="ob-btn ob-btn-primary">
            Set up OWARE <ExternalLink size={14} />
          </button>
        </div>

        <div className="ob-card-foot">
          Setup takes just a few minutes. You can always come back to this
          from Settings → Billing in your dashboard.
        </div>
      </div>
    </div>
  );
}
